/**
 * Hooks del tab Tareas del Panel de Internal. Un solo query (`board`) trae
 * proyectos + tareas; las mutations de drag & drop / edición son optimistas
 * sobre ese cache y el resto invalida.
 */
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import type { Task, TaskProject } from '@/../../shared/internal-tasks';

export type BoardData = { projects: TaskProject[]; tasks: Task[] };

export const useTasksBoard = () =>
  trpc.internalTasks.board.useQuery(undefined, { staleTime: 30_000 });

export const useTaskAssignees = () =>
  trpc.internalTasks.assignees.useQuery(undefined, { staleTime: 5 * 60_000 });

const errMsg = (e: { message?: string }) => e?.message || 'Algo falló';

/** null (borrar campo en el server) → undefined (campo ausente en el cache). */
function stripNulls<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) if (v !== undefined) out[k] = v === null ? undefined : v;
  return out as Partial<T>;
}

export function useTaskMutations() {
  const utils = trpc.useUtils();
  const invalidate = () => utils.internalTasks.board.invalidate();

  /** cancel + snapshot + patch del cache; devuelve el snapshot para rollback. */
  async function patchBoard(fn: (prev: BoardData) => BoardData) {
    await utils.internalTasks.board.cancel();
    const prev = utils.internalTasks.board.getData();
    if (prev) utils.internalTasks.board.setData(undefined, fn(prev));
    return { prev };
  }
  const rollback = (ctx?: { prev?: BoardData }) => {
    if (ctx?.prev) utils.internalTasks.board.setData(undefined, ctx.prev);
  };

  const patchTask = (taskId: string, patch: Partial<Task>) => (prev: BoardData): BoardData => ({
    ...prev,
    tasks: prev.tasks.map((t) => (t.taskId === taskId ? { ...t, ...patch } : t)),
  });

  const moveTask = trpc.internalTasks.moveTask.useMutation({
    onMutate: (vars) => patchBoard(patchTask(vars.taskId, { status: vars.status, position: vars.position })),
    onError: (e, _v, ctx) => { rollback(ctx); toast.error(`No se pudo mover: ${errMsg(e)}`); },
    onSettled: invalidate,
  });

  const updateTask = trpc.internalTasks.updateTask.useMutation({
    onMutate: ({ taskId, ...patch }) => patchBoard(patchTask(taskId, stripNulls(patch) as Partial<Task>)),
    onError: (e, _v, ctx) => { rollback(ctx); toast.error(`No se pudo guardar: ${errMsg(e)}`); },
    onSettled: invalidate,
  });

  const checklistToggle = trpc.internalTasks.checklistToggle.useMutation({
    onMutate: (vars) =>
      patchBoard((prev) => ({
        ...prev,
        tasks: prev.tasks.map((t) =>
          t.taskId === vars.taskId
            ? { ...t, checklist: t.checklist.map((it) => (it.id === vars.itemId ? { ...it, isCompleted: vars.isCompleted } : it)) }
            : t
        ),
      })),
    onError: (e, _v, ctx) => { rollback(ctx); toast.error(errMsg(e)); },
    onSettled: invalidate,
  });

  const simple = (label?: string) => ({
    onSuccess: () => { invalidate(); if (label) toast.success(label); },
    onError: (e: { message?: string }) => toast.error(errMsg(e)),
  });

  const createTask = trpc.internalTasks.createTask.useMutation(simple());
  const deleteTask = trpc.internalTasks.deleteTask.useMutation(simple('Tarea eliminada'));
  const createProject = trpc.internalTasks.createProject.useMutation(simple('Proyecto creado'));
  const updateProject = trpc.internalTasks.updateProject.useMutation(simple());
  const deleteProject = trpc.internalTasks.deleteProject.useMutation(simple('Proyecto eliminado'));
  const checklistAdd = trpc.internalTasks.checklistAdd.useMutation(simple());
  const checklistRemove = trpc.internalTasks.checklistRemove.useMutation(simple());

  return {
    moveTask, updateTask, createTask, deleteTask,
    createProject, updateProject, deleteProject,
    checklistAdd, checklistToggle, checklistRemove,
  };
}
